/* eslint-disable react/no-array-index-key, consistent-return, @typescript-eslint/no-shadow, prettier/prettier, @typescript-eslint/no-unused-vars */
import { useState, useCallback, useEffect } from 'react';
import { IConfigKeyDetail, IImpactReport } from '../../utils/types';
import useService from '../../utils/useService';
import { formatJson } from '../../utils/formatters';

interface UseAnalyzeReturn {
	keyName: string;
	setKeyName: (name: string) => void;
	keyDetail: IConfigKeyDetail | null;
	currentValue: string;
	setCurrentValue: (value: string) => void;
	proposedValue: string;
	setProposedValue: (value: string) => void;
	environment: string;
	setEnvironment: (env: string) => void;
	selectedService: string;
	setSelectedService: (service: string) => void;
	serviceOptions: { label: string; id: string }[];
	isLoading: boolean;
	isLoadingDetail: boolean;
	report: IImpactReport | null;
	handleAnalyze: () => void;
	configKeyOptions: { label: string; id: string }[];
	loadConfigKeys: (inputValue: string) => void;
}

const useAnalyze = (initialKey?: string): UseAnalyzeReturn => {
	const [keyName, setKeyName] = useState(initialKey || '');
	const [keyDetail, setKeyDetail] = useState<IConfigKeyDetail | null>(null);
	const [currentValue, setCurrentValue] = useState('');
	const [proposedValue, setProposedValue] = useState('');
	const [environment, setEnvironment] = useState('CSE');
	const [selectedService, setSelectedService] = useState('');
	const [serviceOptions, setServiceOptions] = useState<{ label: string; id: string }[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isLoadingDetail, setIsLoadingDetail] = useState(false);
	const [report, setReport] = useState<IImpactReport | null>(null);
	const [configKeyOptions, setConfigKeyOptions] = useState<{ label: string; id: string }[]>([]);

	const { fetchConfigKeys, fetchKeyDetail, submitAnalysis } = useService();

	const setKeyNameAndResetService = useCallback((name: string) => {
		setKeyName(name);
		setSelectedService('');
	}, []);

	const loadConfigKeys = useCallback(
		async (inputValue: string) => {
			const result = await fetchConfigKeys({
				pageDetails: { pageNo: 1, pageSize: 20 },
				searchText: inputValue
			});
			setConfigKeyOptions(
				(result.data || []).map((item: any) => ({
					label: item.keyName,
					id: item.keyName
				}))
			);
		},
		[fetchConfigKeys]
	);

	useEffect(() => {
		loadConfigKeys('');
	}, [loadConfigKeys]);

	useEffect(() => {
		if (!keyName) {
			setKeyDetail(null);
			setCurrentValue('');
			setSelectedService('');
			setServiceOptions([]);
			setReport(null);
			return;
		}

		let cancelled = false;
		setIsLoadingDetail(true);

		fetchKeyDetail(keyName, selectedService || undefined)
			.then((detail) => {
				if (cancelled) return;
				setKeyDetail(detail);
				const live = detail.currentValue;
				const fallback = detail.defaultValue;
				setCurrentValue(formatJson(live !== undefined && live !== null ? live : fallback));

				const uniqueServices = Array.from(
					new Set((detail.usages || []).map((u: any) => u.serviceName).filter(Boolean))
				);
				setServiceOptions(uniqueServices.map((s) => ({ label: s, id: s })));
				// Default to the first service so Current Value populates without an explicit pick.
				// Users can still switch via the dropdown.
				if (!selectedService && uniqueServices.length > 0) {
					setSelectedService(uniqueServices[0]);
				}
			})
			.finally(() => {
				if (!cancelled) setIsLoadingDetail(false);
			});

		return () => {
			cancelled = true;
		};
	}, [keyName, selectedService, fetchKeyDetail]);

	const handleAnalyze = useCallback(async () => {
		if (!keyName || !proposedValue) return;

		setIsLoading(true);
		setReport(null);

		try {
			const result = await submitAnalysis({
				keyName,
				currentValue: currentValue || undefined,
				proposedValue,
				environment: environment.toLowerCase() as 'cse' | 'production'
			});
			setReport(result.report);
		} finally {
			setIsLoading(false);
		}
	}, [keyName, currentValue, proposedValue, environment, submitAnalysis]);

	return {
		keyName,
		setKeyName: setKeyNameAndResetService,
		keyDetail,
		currentValue,
		setCurrentValue,
		proposedValue,
		setProposedValue,
		environment,
		setEnvironment,
		selectedService,
		setSelectedService,
		serviceOptions,
		isLoading,
		isLoadingDetail,
		report,
		handleAnalyze,
		configKeyOptions,
		loadConfigKeys
	};
};

export default useAnalyze;
